/* eslint-disable sonarjs/cognitive-complexity */
import React, {type FC, useCallback, useState} from 'react';
import {IProduct} from '../../../../types/galleryTypes';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import s from './ProductOptions.scss';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import {useGlobals, withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {Experiments} from '../../../../constants';
import {ProductColorOption} from './ProductColorOption/ProductColorOption';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {getAllVisibleColorOptions, getAllVisibleTextualOptions} from './utils/getVisibleOptions';
import {ProductTextualOption} from './ProductTextualOptions/ProductTextualOption';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {useOptionFocusManagement} from './utils/hooks/useOptionFocusManagement';

type IProductOption = IProduct['options'][0];
export interface ProductOptionsChangeData {
  optionType: ProductOptionType;
}

export interface IProductOptionsProps extends IGalleryGlobalProps {
  product: IProduct;
  selectionIds: number[];
  isLoading?: boolean;
  readOnly?: boolean;

  onSelectionIdsChange(
    optionSelections: IProductOptionsProps['selectionIds'],
    changeData: ProductOptionsChangeData
  ): Promise<void>;

  variantsAvailability: IProductSelectionAvailabilityMap;
}

export interface OptionRenderParams {
  option: IProduct['options'][number];
  availability: IProductSelectionAvailabilityMap[string];
  url?: string;
}

export enum DataHook {
  ProductOption = 'product-option',
  DropdownOption = 'product-options-dropdown',
  ColorOption = 'product-options-color',
  ColorOptionMore = 'product-options-color-more',
  OptionWrapper = 'product-option-wrapper',
  ButtonOption = 'product-option-button',
  ButtonOptionMore = 'product-options-button-more',
  ProductOptionLoader = 'product-option-loader',
}

const ProductOptionsComponent: FC<IProductOptionsProps> = ({
  product,
  selectionIds,
  isLoading,
  onSelectionIdsChange,
  variantsAvailability,
  readOnly,
}) => {
  const {productsManifest} = useGlobals();
  const {experiments} = useExperiments();
  const isQualityOptionsStylingChangesEnabled = experiments.enabled(Experiments.EnableQualityOptionsStylingChanges);
  const shouldShowFirstProductOptionInGallery = experiments.enabled(Experiments.ShouldShowFirstProductOptionInGallery);
  const [latestActiveOptionId, setLatestActiveOptionId] = useState<string>('');

  const isOptionSelected = useCallback(
    (option: IProductOption) => option.selections.find(({id}) => selectionIds.includes(id)) !== undefined,
    [selectionIds]
  );

  const {registerOptionRef} = useOptionFocusManagement({product, isOptionSelected});

  const shouldRenderLoader = useCallback(
    (optionId: string) => {
      return isLoading && latestActiveOptionId !== optionId;
    },
    [isLoading, latestActiveOptionId]
  );

  const handleOptionChange = useCallback(
    async (selectedOptionIds: number[], changeData: ProductOptionsChangeData, optionId: string) => {
      setLatestActiveOptionId(optionId);
      return onSelectionIdsChange(selectedOptionIds, changeData);
    },
    [onSelectionIdsChange]
  );

  const renderColorOption = useCallback(
    (option: IProductOption) => {
      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.color]: isQualityOptionsStylingChangesEnabled,
            [s.oldColor]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductColorOption
            url={productsManifest[product.id].url}
            option={option}
            selectionsAvailability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            onSelectionIdsChange={(selectedOptionIds: number[], changeData: ProductOptionsChangeData) =>
              handleOptionChange(selectedOptionIds, changeData, option.id)
            }
            isLoading={shouldRenderLoader(option.id)}
          />
        </div>
      );
    },
    [
      handleOptionChange,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      selectionIds,
      shouldRenderLoader,
      variantsAvailability,
      product,
      registerOptionRef,
    ]
  );

  const getColorOptions = useCallback(() => {
    const colorOptions = getAllVisibleColorOptions(product.options);
    return colorOptions.map((option) => renderColorOption(option));
  }, [renderColorOption, product.options]);

  const renderTextualOption = useCallback(
    (option: IProductOption) => {
      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.dropdown]: isQualityOptionsStylingChangesEnabled,
            [s.oldDropdown]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductTextualOption
            productUrl={productsManifest[product.id].url}
            option={option}
            availability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            readOnly={readOnly}
            isLoading={shouldRenderLoader(option.id)}
            onSelectionIdsChange={(selectedOptionIds: number[], changeData: ProductOptionsChangeData) =>
              handleOptionChange(selectedOptionIds, changeData, option.id)
            }
          />
        </div>
      );
    },
    [
      handleOptionChange,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      selectionIds,
      shouldRenderLoader,
      variantsAvailability,
      product,
      readOnly,
      registerOptionRef,
    ]
  );

  const getTextualOptions = useCallback(() => {
    const textualOptions = getAllVisibleTextualOptions(product.options);
    return textualOptions.map((option) => renderTextualOption(option));
  }, [renderTextualOption, product.options]);

  const getFirstOption = useCallback(() => {
    const firstOption = product.options[0];
    if (!firstOption) {
      return null;
    }
    const optionType = firstOption.optionType;

    if (optionType === ProductOptionType.COLOR) {
      return renderColorOption(firstOption);
    } else {
      return renderTextualOption(firstOption);
    }
  }, [renderColorOption, renderTextualOption, product.options]);

  return shouldShowFirstProductOptionInGallery ? (
    <>
      <ConditionalRender by="showFirstOptionAnyType">{getFirstOption()}</ConditionalRender>
      <ConditionalRender by="showColorOption">{getColorOptions()}</ConditionalRender>
      <ConditionalRender by="showTextualOptions">{getTextualOptions()}</ConditionalRender>
    </>
  ) : (
    <>
      {getColorOptions()}
      <ConditionalRender by="showTextualOptionsWhenShowFirstOptionAnyTypeExperimentIsDisabled">
        {getTextualOptions()}
      </ConditionalRender>
    </>
  );
};

export const ProductOptions = withGlobals(ProductOptionsComponent);
